import { useState } from "preact/hooks";
import type { Setting, Texts, Shipment } from "@/type";
import TrackingTimeline from "./TrackingTimeline";
import ShippingInfoCard from "./ShippingInfoCard";
import CustomerInfoCard from "./CustomerInfoCard";
import PackageItemsCard from "./PackageItemsCard";

interface OrderResultProps {
  settings: Setting;
  texts: Texts;
  shipments: Shipment[];
  onBack: () => void;
}

export default function OrderResult({ settings, texts, shipments, onBack }: OrderResultProps) {
  const { style, font_family } = settings;
  const { colors, general_content_text_size, layout } = style;
  const [activeTab, setActiveTab] = useState(0);

  const showTabs = shipments.length > 1;
  const currentShipment = shipments[activeTab];

  if (!currentShipment) return null;

  return (
    <div
      className="min-h-screen py-10 px-4"
      style={{
        fontFamily: font_family.font,
      }}
    >
      <div className="mx-auto" style={{ maxWidth: layout.max_width }}>
        <div className="relative">
          {/* 返回按钮 */}
          <button
            className="flex items-center gap-1 transition-opacity hover:opacity-70 absolute top-1/2 -translate-y-1/2 left-0"
            style={{
              color: colors.text_color.tracking_page_title_text,
              fontSize: general_content_text_size.shipping_status_text,
            }}
            onClick={onBack}
          >
            <svg
              width="16"
              height="auto"
              viewBox="0 0 13 10"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                fill: colors.text_color.tracking_page_title_text,
              }}
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M13 4.75C13 5.16421 12.6642 5.5 12.25 5.5L2.56078 5.5L5.2807 8.21964C5.57361 8.51252 5.57363 8.98739 5.28075 9.2803C4.98787 9.57321 4.513 9.57324 4.22009 9.28036L0.219692 5.28036C0.0790231 5.1397 -4.9847e-06 4.94892 -4.976e-06 4.75C-4.96731e-06 4.55107 0.0790232 4.3603 0.219692 4.21964L4.22009 0.219643C4.513 -0.0732356 4.98787 -0.0732117 5.28075 0.219696C5.57363 0.512604 5.57361 0.987478 5.2807 1.28036L2.56078 4L12.25 4C12.6642 4 13 4.33579 13 4.75Z"
              />
            </svg>
          </button>

          {/* 标题 */}
          <h1
            className="font-bold mb-8 text-center text-2xl"
            style={{
              color: colors.text_color.tracking_page_title_text,
              fontSize: general_content_text_size.tracking_page_title_text,
            }}
          >
            {texts.your_order_status}
          </h1>
        </div>

        {/* Tab 切换栏（多包裹时显示） */}
        {showTabs && (
          <div
            className="flex mx-auto mb-8"
            style={{
              maxWidth: 360,
              boxShadow: "inset 0px -1px 0px 0px #e3e3e3",
            }}
          >
            {shipments.map((shipment, idx) => {
              const isActive = activeTab === idx;
              return (
                <button
                  key={idx}
                  className="flex-1 flex items-center justify-center relative"
                  style={{
                    height: 52,
                    color: colors.text_color.tab_text_color,
                    fontSize: general_content_text_size.button_text,
                    fontWeight: 650,
                    fontFamily: font_family.font,
                  }}
                  onClick={() => setActiveTab(idx)}
                >
                  {shipment.label || `${texts.shipment} ${idx + 1}`}
                  {isActive && (
                    <span
                      className="absolute bottom-0 left-0 right-0"
                      style={{
                        height: 3,
                        borderRadius: 4,
                        backgroundColor: colors.general_color.tab || "#131313",
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* 当前包裹内容 */}
        <div
          className="flex flex-col lg:flex-row gap-6"
          style={{ marginBottom: layout.spacing_between_cards * 2 }}
        >
          {/* 左侧：时间线 */}
          <div
            className="flex-1 p-6 shadow-sm"
            style={{
              backgroundColor: colors.general_color.information_card_background,
              borderRadius: style.borders.information_card_corner_radius,
              border: `1px solid ${colors.general_color.information_card_background}`,
            }}
          >
            <TrackingTimeline settings={settings} texts={texts} shipment={currentShipment} />
          </div>

          {/* 右侧：信息卡片 */}
          <div className="lg:w-80 flex flex-col" style={{ gap: layout.spacing_between_cards }}>
            <ShippingInfoCard settings={settings} texts={texts} shipment={currentShipment} />
            <CustomerInfoCard settings={settings} texts={texts} shipment={currentShipment} />
            {currentShipment.package_items && currentShipment.package_items.length > 0 && (
              <PackageItemsCard
                settings={settings}
                texts={texts}
                items={currentShipment.package_items}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
